/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { SlideOver } from '@/shared/ui/SlideOver'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Plus, Save, Pencil, Trash2, ShieldCheck } from 'lucide-react'
import { toast } from '@/store/useToastStore'
import { useCan } from '@/shared/permissions'
import { getApiError } from '@/api/client'
import {
  useRoles, useAllPermissions, useMappedRoles, useCreateRole, useAssignPermissions,
  usePermissionCatalog, useRbacRoles, useRbacRole, useCreateRbacRole, useUpdateRbacRole, useDeleteRbacRole, useSetRbacRolePermissions,
} from '@/api/hooks'
import { toArray, idsOf, permKey, describePermission, sameSet, type Role, type Permission, type PermView } from './rolesUtils'
import { PermissionEditor } from './PermissionEditor'
import { SearchBox, ErrorNote, Center } from './rolesUi'

/**
 * One screen for roles + permissions: pick a role on the left, set what it may do on the right.
 * Platform admins use the v2 RBAC API; school admins keep their own endpoints.
 */
export function RoleAccessTab({ isSchoolAdmin }: { isSchoolAdmin: boolean }) {
  const confirm = useConfirm()
  const can = useCan()
  // Managing roles needs write access to the role/RBAC API; read-only admins can still look.
  const canCreate = can(['POST /v2/admin/rbac/*', 'POST /admin/role/*', 'POST /v2/admin/role/*', 'POST /admin/access-control-for-school-admin/*'])
  const canWrite  = can(['PUT /v2/admin/rbac/*', 'POST /admin/role/*', 'POST /v2/admin/role/*', 'POST /admin/access-control-for-school-admin/*'])
  const canDelete = can(['DELETE /v2/admin/rbac/*', 'DELETE /admin/role/*'])

  // ── data ────────────────────────────────────────────────────────────────
  const rbacRolesQ = useRbacRoles()
  const schoolRolesQ = useRoles()
  const rolesQ = isSchoolAdmin ? schoolRolesQ : rbacRolesQ
  // Platform admins pick from the grantable endpoint keys (who-am-i); school admins keep the legacy list.
  const catalogQ = usePermissionCatalog(!isSchoolAdmin)
  const legacyQ = useAllPermissions(isSchoolAdmin)
  const permsQ = (isSchoolAdmin ? legacyQ : catalogQ) as typeof catalogQ
  const mappedQ = useMappedRoles()

  const roles: Role[] = useMemo(() => toArray(rolesQ.data, 'roles'), [rolesQ.data])
  const perms: Permission[] = useMemo(() => toArray(permsQ.data, 'permissions'), [permsQ.data])
  const mapped: any[] = useMemo(() => toArray(mappedQ.data), [mappedQ.data])
  const views: PermView[] = useMemo(() => perms.map(describePermission), [perms])

  const createSchoolRole = useCreateRole()
  const assignSchool = useAssignPermissions()
  const createRole = useCreateRbacRole()
  const updateRole = useUpdateRbacRole()
  const deleteRole = useDeleteRbacRole()
  const setRolePerms = useSetRbacRolePermissions()

  // ── selection ───────────────────────────────────────────────────────────
  const [selRole, setSelRole] = useState<number | null>(null)
  const [roleSearch, setRoleSearch] = useState('')
  const roleDetailQ = useRbacRole(isSchoolAdmin ? null : selRole)

  useEffect(() => { if (selRole == null && roles.length) setSelRole(roles[0].id) }, [roles, selRole])
  useEffect(() => { if (selRole != null && roles.length && !roles.some(r => r.id === selRole)) setSelRole(roles[0]?.id ?? null) }, [roles, selRole])

  // The RBAC API identifies permissions by key; the catalog gives us id↔key so the editor can
  // keep working with ids internally and convert at the edges.
  const idByKey = useMemo(() => new Map(perms.map(p => [permKey(p), p.id] as const)), [perms])
  const keyById = useMemo(() => new Map(perms.map(p => [p.id, permKey(p)] as const)), [perms])

  // A role's permissions may come back as keys ("GET /admin/…") or as objects with a key and/or id.
  const resolveIds = (list: any[]): Set<number> => new Set(
    list.map(e => {
      if (typeof e === 'string') return idByKey.get(e)
      const k = e?.key ?? e?.permission_key ?? e?.permission?.key ?? e?.name ?? e?.permission?.name
      return (k != null ? idByKey.get(String(k)) : undefined) ?? e?.permission_id ?? e?.permission?.id ?? e?.id
    }).filter((x): x is number => typeof x === 'number'),
  )

  // Permissions currently saved on the server for the selected role.
  const saved = useMemo(() => {
    if (selRole == null) return new Set<number>()
    if (isSchoolAdmin) {
      const m = mapped.find((x: any) => (x.role_id ?? x.role?.id) === selRole)
      return new Set(idsOf(m?.permissions ?? m?.role?.permissions ?? []))
    }
    const d: any = (roleDetailQ.data as any)?.data ?? roleDetailQ.data
    return resolveIds(d?.permission_keys ?? d?.permissions ?? d?.role?.permissions ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selRole, mapped, roleDetailQ.data, isSchoolAdmin, idByKey])

  // Keys already on the role that this admin can't see in the catalog (they don't hold them
  // themselves). They're sent back on save so editing never silently revokes them.
  const hiddenKeys = useMemo(() => {
    if (isSchoolAdmin || selRole == null || !perms.length) return [] as string[]
    const d: any = (roleDetailQ.data as any)?.data ?? roleDetailQ.data
    const list: any[] = d?.permission_keys ?? d?.permissions ?? d?.role?.permissions ?? []
    return list
      .map(e => typeof e === 'string' ? e : String(e?.key ?? e?.permission_key ?? e?.permission?.key ?? e?.name ?? ''))
      .filter(k => k && !idByKey.has(k))
  }, [isSchoolAdmin, selRole, perms.length, roleDetailQ.data, idByKey])

  // The working copy the user is editing.
  const [draft, setDraft] = useState<Set<number>>(new Set())
  useEffect(() => { setDraft(new Set(saved)) }, [saved])
  const dirty = !sameSet(draft, saved)
  const changes = useMemo(() => {
    let added = 0, removed = 0
    draft.forEach(id => { if (!saved.has(id)) added++ })
    saved.forEach(id => { if (!draft.has(id)) removed++ })
    return { added, removed }
  }, [draft, saved])

  const detailLoading = !isSchoolAdmin && selRole != null && roleDetailQ.isLoading

  const pickRole = async (id: number) => {
    if (id === selRole) return
    if (dirty && !(await confirm({
      title: 'Discard unsaved changes?',
      description: 'You changed permissions for this role but did not save. Switching roles will lose those changes.',
      confirmLabel: 'Discard', variant: 'danger',
    }))) return
    setSelRole(id)
  }

  const save = async () => {
    if (selRole == null || !dirty) return
    const role = roles.find(r => r.id === selRole)
    if (!(await confirm({
      title: 'Save permission changes?',
      description: `"${role?.name}" will get ${changes.added} new and lose ${changes.removed} permission${changes.removed === 1 ? '' : 's'}. Users with this role are affected immediately.`,
      confirmLabel: 'Save changes', variant: 'primary',
    }))) return
    const ids = [...draft]
    try {
      if (isSchoolAdmin) await assignSchool.mutateAsync({ role_id: selRole, permissions: ids, permission_ids: ids })
      else {
        const keys = [...ids.map(id => keyById.get(id)).filter((k): k is string => !!k), ...hiddenKeys]
        await setRolePerms.mutateAsync({ id: selRole, permission_keys: keys, permission_ids: [] })
      }
      toast.success('Permissions saved')
    } catch (e) { toast.error(getApiError(e)) }
  }

  // ── role create / edit / delete ─────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const openCreate = () => { setEditing(null); setName(''); setDesc(''); setFormOpen(true) }
  const openEdit = (r: Role) => { setEditing(r); setName(r.name); setDesc(r.description ?? ''); setFormOpen(true) }

  const saveRole = async () => {
    if (!name.trim()) return
    try {
      if (isSchoolAdmin) await createSchoolRole.mutateAsync({ name: name.trim(), description: desc })
      else if (editing) await updateRole.mutateAsync({ id: editing.id, name: name.trim(), description: desc })
      else {
        const res: any = await createRole.mutateAsync({ name: name.trim(), description: desc, city_id: null })
        const newId = res?.data?.id ?? res?.id
        if (newId) setSelRole(newId)
      }
      toast.success(editing ? 'Role updated' : 'Role created')
      setFormOpen(false)
    } catch (e) { toast.error(getApiError(e)) }
  }

  const removeRole = async (r: Role) => {
    if (r.is_system) { toast.error('System roles cannot be deleted.'); return }
    if (!(await confirm({
      title: `Delete "${r.name}"?`,
      description: 'Users who have this role will lose its permissions. This cannot be undone.',
      confirmLabel: 'Delete role', variant: 'danger',
    }))) return
    try { await deleteRole.mutateAsync(r.id); toast.success('Role deleted') }
    catch (e) { toast.error(getApiError(e)) }
  }

  const shownRoles = roles.filter(r => `${r.name} ${r.description ?? ''}`.toLowerCase().includes(roleSearch.trim().toLowerCase()))
  const current = roles.find(r => r.id === selRole)
  const saving = assignSchool.isPending || setRolePerms.isPending

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-start">
        {/* ── Roles list ── */}
        <Card className="lg:sticky lg:top-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900">Roles <span className="text-gray-400 font-normal text-sm">({roles.length})</span></h3>
            {canCreate && <Button size="sm" icon={<Plus size={13} />} onClick={openCreate}>New</Button>}
          </div>
          {roles.length > 6 && (
            <SearchBox value={roleSearch} onChange={setRoleSearch} placeholder="Search roles…" className="mb-3" />
          )}
          {rolesQ.isLoading ? <Center /> : rolesQ.isError ? <ErrorNote err={rolesQ.error} onRetry={() => rolesQ.refetch()} /> :
            roles.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <ShieldCheck className="mx-auto text-gray-300" size={32} />
                <p className="text-sm text-gray-500">No roles yet. A role is a named set of permissions you can give to users.</p>
                {canCreate && <Button size="sm" icon={<Plus size={13} />} onClick={openCreate}>Create your first role</Button>}
              </div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto -mx-1 px-1">
                {shownRoles.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No role matches “{roleSearch}”</p>}
                {shownRoles.map(r => {
                  const active = r.id === selRole
                  const count = active ? draft.size : (r.permissions?.length ?? r.permission_count)
                  return (
                    <div key={r.id} className="group relative">
                      <button onClick={() => pickRole(r.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors border ${active ? 'bg-brand-50 border-brand-200' : 'border-transparent hover:bg-gray-50'}`}>
                        <div className="flex items-center justify-between gap-2 pr-7">
                          <span className={`text-sm truncate ${active ? 'font-semibold text-brand-700' : 'font-medium text-gray-800'}`}>{r.name}</span>
                          {count != null && <span className="text-[11px] text-gray-400 shrink-0">{count} perms</span>}
                        </div>
                        {r.description && <p className="text-xs text-gray-400 truncate mt-0.5 pr-7">{r.description}</p>}
                      </button>
                      {!isSchoolAdmin && canDelete && (
                        <button onClick={() => removeRole(r)} aria-label={`Delete ${r.name}`}
                          title={r.is_system ? 'System roles cannot be deleted' : `Delete ${r.name}`}
                          className={`absolute right-2 top-2.5 w-6 h-6 rounded-md flex items-center justify-center text-gray-400 transition ${r.is_system ? 'opacity-40 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-red-50 hover:text-red-500'}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
        </Card>

        {/* ── Permission editor ── */}
        <Card className="min-w-0">
          {!current ? (
            <p className="text-sm text-gray-400 py-16 text-center">{rolesQ.isLoading ? 'Loading…' : 'Select or create a role to manage its permissions.'}</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg truncate">{current.name}</h3>
                  <p className="text-sm text-gray-500">{current.description || 'No description'}</p>
                </div>
                {!isSchoolAdmin && (canWrite || canDelete) && (
                  <div className="flex items-center gap-1 shrink-0">
                    {canWrite && <Button size="sm" variant="outline" icon={<Pencil size={13} />} onClick={() => openEdit(current)}>Edit</Button>}
                    {canDelete && <Button size="sm" variant="outline" icon={<Trash2 size={13} />} disabled={!!current.is_system}
                      title={current.is_system ? 'System roles cannot be deleted' : undefined} onClick={() => removeRole(current)}>Delete</Button>}
                  </div>
                )}
              </div>

              {permsQ.isLoading || detailLoading ? <Center /> :
                permsQ.isError ? <ErrorNote err={permsQ.error} onRetry={() => permsQ.refetch()} /> :
                roleDetailQ.isError ? <ErrorNote err={roleDetailQ.error} onRetry={() => roleDetailQ.refetch()} /> :
                <PermissionEditor views={views} draft={draft} setDraft={setDraft} />}

              {/* unsaved-changes bar */}
              {dirty && (
                <div className="sticky bottom-3 mt-4 flex items-center justify-between gap-3 rounded-xl bg-gray-900 text-white px-4 py-3 shadow-lg z-10">
                  <span className="text-sm">
                    Unsaved changes: <b className="text-green-400">+{changes.added}</b> added, <b className="text-red-400">−{changes.removed}</b> removed
                  </span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="!text-gray-200 hover:!bg-white/10" onClick={() => setDraft(new Set(saved))}>Discard</Button>
                    <Button size="sm" icon={<Save size={13} />} loading={saving} onClick={save}>Save changes</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <SlideOver open={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Edit role' : 'New role'}>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Role name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bookings viewer" autoFocus
              onKeyDown={e => e.key === 'Enter' && saveRole()}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Description</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} placeholder="What is this role for?"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          {!editing && <p className="text-xs text-gray-400">After creating the role you can set its permissions on the main screen.</p>}
          <Button className="w-full justify-center" disabled={!name.trim()}
            loading={createRole.isPending || updateRole.isPending || createSchoolRole.isPending} onClick={saveRole}>
            {editing ? 'Save changes' : 'Create role'}
          </Button>
        </div>
      </SlideOver>
    </>
  )
}
